import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GradientButton from './GradientButton';
import { FaBars, FaTimes } from 'react-icons/fa';
import { List, ListItem, ListItemButton, ListItemText, Divider, ListItemAvatar, Avatar, LinearProgress, Fab, CircularProgress, Snackbar, Alert, Button, Tooltip, Checkbox, SwipeableDrawer, Radio, TextField, FormControlLabel, IconButton, Modal, Box, Typography, Accordion, AccordionSummary, AccordionDetails, ListItemIcon, Dialog, DialogContent, DialogTitle } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import GroupIcon from '@mui/icons-material/Group';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from 'lodash';
import { API_BASE_URL } from '../config';
import VenmoIcon from '@mui/icons-material/AccountBalance'; // You might want to use a custom Venmo icon instead
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid'; // Import the uuid library
import { checkAuthAndRedirect } from '../utils/auth';
import { checkSubscriptionAndRedirect } from '../utils/subscriptionHelpers';
import { 
  getEffectiveValue, 
  isFieldEdited,
  updateReceiptField,
  updateReceiptItemField,
  fetchReceipts,
  fetchReceipt,
  fetchReceiptItems,
  deleteReceiptItem,
  applyLocalReceiptUpdate,
  applyLocalItemUpdate,
  formatDateForAPI,
  handleFileUpload,
  parseDate,
  formatDate
} from '../utils/receiptsUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import SplitReceipts from './receiptsdetail/SplitReceipts';
import UploadFab from './receiptsdetail/UploadFab';
import ImageModal from './receiptsdetail/ImageModal';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear'; // Add this import at the top of the file
import EditableField from './shared/EditableField';
import CategorySelector from './shared/CategorySelector';
import PWAInstallBanner from './shared/PWAInstallBanner';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useUpload } from '../contexts/UploadContext';
import { ReceiptItemRow } from './receiptsdetail/ReceiptItemRow';

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === 'light' ? '#888888' : '#ffffff',
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));

// Add this array of colors for splits
const splitColors = ['#3f51b5', '#f50057', '#00a152', '#ff9800', '#9c27b0', '#00bcd4'];

// Update the width of the right pane
const rightPaneWidth = '384px'; // This is equivalent to 'w-96' in Tailwind

// Add this helper function near the top of the file, after the imports
const parseCurrencyString = (str) => {
  if (!str) return 0;
  return parseFloat(str.toString().replace(/[$,]/g, ''));
};

function ReceiptsDetail({ className = '' }) {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [items, setItems] = useState([]);
  const [showLeftPane, setShowLeftPane] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [receipts, setReceipts] = useState([]);
  const [isLoadingReceipts, setIsLoadingReceipts] = useState(false);
  const [isLoadingSelectedReceipt, setIsLoadingSelectedReceipt] = useState(false);
  const [errorToast, setErrorToast] = useState({ open: false, message: '' });
  const [successToast, setSuccessToast] = useState({ open: false, message: '' });
  const fileInputRef = useRef(null);
  const [isSplitting, setIsSplitting] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [splits, setSplits] = useState([]);
  const [splitItems, setSplitItems] = useState({});
  const [splitNames, setSplitNames] = useState([]);
  const [splitTip, setSplitTip] = useState(false);
  const [splitTax, setSplitTax] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [receiptImage, setReceiptImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReceipts, setFilteredReceipts] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, itemId: null });
  const { handleUpload, isUploading, uploadProgress } = useUpload();

  // Add this constant for the top offset
  const topOffset = '64px'; // Adjust this value to match the height of your top bar

  // Add effect to handle upload completion
  useEffect(() => {
    // Check if any uploads have completed
    const hasCompletedUploads = uploadProgress?.some(file => file.status === 'completed');
    
    if (hasCompletedUploads) {
      // Refresh the receipts list
      fetchReceiptsList();
    }
  }, [uploadProgress]); // Only depend on uploadProgress changes

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowLeftPane(!mobile);
      if (mobile) {
        setShowPreview(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Effect for initial receipts load
  useEffect(() => {
    const loadReceipts = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth?mode=login');
        return;
      }

      setIsLoadingReceipts(true);
      try {
        const receiptsData = await fetchReceipts(token);
        setReceipts(receiptsData);
      } catch (error) {
        console.error('Error loading receipts:', error);
        setErrorToast({ open: true, message: 'Failed to load receipts. Please try again.' });
      } finally {
        setIsLoadingReceipts(false);
      }
    };

    loadReceipts();
  }, []); // Only run on mount

  // Effect for selected receipt
  useEffect(() => {
    const loadSelectedReceipt = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth?mode=login');
        return;
      }

      // If we have a receiptId, fetch that specific receipt and its items
      if (receiptId) {
        setIsLoadingSelectedReceipt(true);
        try {
          // Use the utility functions to fetch receipt and items
          const [receiptData, itemsData] = await Promise.all([
            fetchReceipt(token, receiptId),
            fetchReceiptItems(token, receiptId)
          ]);
          
          if (receiptData) {
            setSelectedReceipt(receiptData);
            setReceiptImage(receiptData.receipt_image_uri || null);
          } else {
            setErrorToast({ open: true, message: 'Receipt not found' });
          }

          setItems(itemsData || []);
        } catch (error) {
          console.error('Error loading receipt:', error);
          setErrorToast({ open: true, message: 'Failed to load receipt. Please try again.' });
        } finally {
          setIsLoadingSelectedReceipt(false);
        }
      } else {
        // Clear selected receipt when no receiptId is present
        setSelectedReceipt(null);
        setItems([]);
        setReceiptImage(null);
      }
    };

    loadSelectedReceipt();
  }, [receiptId, navigate]); // Only run when receiptId changes

  useEffect(() => {
    checkSubscriptionAndRedirect(
      navigate,
      () => {/* Do nothing, user has subscription */},
      () => navigate('/checkout')
    );
  }, [navigate]);

  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase();
    const filtered = receipts
      .filter(receipt => {
        return (
          receipt.merchant_name.toLowerCase().includes(lowercasedFilter) ||
          (receipt.date && formatDate(receipt.date).toLowerCase().includes(lowercasedFilter)) ||
          receipt.uuid.toLowerCase().includes(lowercasedFilter)
        );
      })
      .sort((a, b) => {
        // Get the effective dates (edited or original)
        const dateA = parseDate(getEffectiveValue(a, 'date'));
        const dateB = parseDate(getEffectiveValue(b, 'date'));
        
        // Handle cases where dates might be missing or invalid
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Sort in descending order (newest first)
        return dateB.getTime() - dateA.getTime();
      });
    
    setFilteredReceipts(filtered);
  }, [searchTerm, receipts]);

  const fetchReceiptsList = async () => {
    const isAuthenticated = await checkAuthAndRedirect(navigate);
    if (!isAuthenticated) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    setIsLoadingReceipts(true);
    try {
      const receiptsData = await fetchReceipts(token);
      setReceipts(receiptsData);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setErrorToast({ open: true, message: 'Failed to fetch receipts. Please try again.' });
    } finally {
      setIsLoadingReceipts(false);
    }
  };

  const handleReceiptSelect = (receipt) => {
    if (receipt.uuid === receiptId) {
      return;
    }
    navigate(`/receipts/${receipt.uuid}`);
    
    if (isMobile) {
      setShowLeftPane(false);
    }
  };

  const handleItemEditLocal = (id, field, value) => {
    // This function updates the local state immediately for a smoother UX
    // before the server response comes back
    setItems(applyLocalItemUpdate(items, id, field, value));
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    await handleUpload(files, token, async () => {
      await fetchReceiptsList();
    });
  };

  const handleExportCSV = () => {
    if (!selectedReceipt || !items.length) return;
    const csvContent = [
      ['Item', 'High Level Category', 'Detailed Category', 'Price'],
      ...items.map(item => [
        item.item_name,
        item.high_level_category,
        item.detailed_category,
        item.item_price
      ]),
    ].map(e => e.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      const formattedDate = selectedReceipt.date ? new Date(selectedReceipt.date).toISOString().split('T')[0] : 'unknown-date';
      const fileName = `${selectedReceipt.merchant_name}_${formattedDate}_receipt.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSplitReceipt = () => {
    // First, set isSplitting to true to trigger the drawer animation
    setIsSplitting(true);
    
    // Use setTimeout to delay the rest of the setup until after the animation starts
    setTimeout(() => {
      setSelectedItems({});
      setSplits([{ items: {}, total: 0 }]);
      setSplitNames(['Split 1']);
    }, 50); // A small delay to ensure the animation starts first
  };

  const calculateTotal = (splitItems, splitIndex, tipSplit, taxSplit, splitCount) => {
    let total = items
      .filter(item => splitItems[item.item_id] === splitIndex)
      .reduce((sum, item) => {
        const price = parseCurrencyString(item.item_price || 0);
        return item.is_maybe_discount ? sum - price : sum + price;
      }, 0);

    if (tipSplit && selectedReceipt?.maybe_receipt_tip) {
      total += parseCurrencyString(selectedReceipt.maybe_receipt_tip) / splitCount;
    }
    if (taxSplit && selectedReceipt?.maybe_receipt_tax) {
      total += parseCurrencyString(selectedReceipt.maybe_receipt_tax) / splitCount;
    }

    return total;
  };

  const updateSplitTotals = (newSplitTip, newSplitTax, splitCount = splits.length) => {
    setSplits(prevSplits => 
      prevSplits.map((split, index) => ({
        ...split,
        total: calculateTotal(splitItems, index, newSplitTip, newSplitTax, splitCount)
      }))
    );
  };

  const debouncedUpdateSplitTotals = debounce(updateSplitTotals, 100);

  const handleItemSelect = (itemId) => {
    const newSplitItems = { ...splitItems };
    const currentSplitIndex = splits.length - 1;

    if (newSplitItems[itemId] === currentSplitIndex) {
      delete newSplitItems[itemId];
    } else if (newSplitItems[itemId] === undefined) {
      newSplitItems[itemId] = currentSplitIndex;
    } else {
      // If it's already selected for a previous split, move it to the current split
      newSplitItems[itemId] = currentSplitIndex;
    }
    
    setSplitItems(newSplitItems);

    // Update all splits
    setSplits(prevSplits => 
      prevSplits.map((split, index) => ({
        ...split,
        total: calculateTotal(newSplitItems, index, splitTip, splitTax, splits.length)
      }))
    );
  };

  const handleAddSplit = () => {
    const newSplitCount = splits.length + 1;
    setSplits(prevSplits => [
      ...prevSplits.map((split, index) => ({
        ...split,
        total: calculateTotal(splitItems, index, splitTip, splitTax, newSplitCount)
      })),
      { items: {}, total: calculateTotal(splitItems, newSplitCount - 1, splitTip, splitTax, newSplitCount) }
    ]);
    setSplitNames(prevNames => [...prevNames, `Split ${newSplitCount}`]);
  };

  const handleSplitNameChange = (index, newName) => {
    const newSplitNames = [...splitNames];
    newSplitNames[index] = newName;
    
    setSplitNames(newSplitNames);
  };

  const handleFinishSplit = () => {
    setShowSplitModal(true);
  };

  const handleCloseSplitModal = () => {
    setShowSplitModal(false);
    setIsSplitting(false);
    
    // Reset split data
    setTimeout(() => {
      setSelectedItems({});
      setSplits([]);
      setSplitItems({});
      setSplitNames([]);
      setSplitTip(false);
      setSplitTax(false);
    }, 300);
  };

  const handleExportToVenmo = (splitIndex) => {
    // Implement Venmo export logic here
    console.log(`Exporting split ${splitIndex} to Venmo`);
    const currSplit = splits[splitIndex];
    console.log(currSplit);

    const splitItemsList = items.filter(item => splitItems[item.item_id] === splitIndex);
    console.log(splitItemsList);

    // You would typically open a new window or redirect to a Venmo URL here
    // Open a new tab with Venmo URL
    const venmoBaseUrl = 'https://venmo.com/?txn=pay';
    const amount = splits[splitIndex].total.toFixed(2);
    const note = encodeURIComponent(`Split for ${selectedReceipt.merchant_name}:
      Items: 
      ${splitItemsList.map(item => item.item_name).join('\n')}
      `);
    
    const venmoUrl = `${venmoBaseUrl}&amount=${amount}&note=${note}`;
    console.log(venmoUrl);
    window.open(venmoUrl, '_blank');
  };

  const handleSplitTipChange = (event) => {
    const newSplitTip = event.target.checked;
    setSplitTip(newSplitTip);
    updateSplitTotals(newSplitTip, splitTax);
  };

  const handleSplitTaxChange = (event) => {
    const newSplitTax = event.target.checked;
    setSplitTax(newSplitTax);
    updateSplitTotals(splitTip, newSplitTax);
  };

  const drawerHeight = '300px';

  useEffect(() => {
    if (isSplitting) {
      setSplits(prevSplits => 
        prevSplits.map((split, index) => ({
          ...split,
          total: calculateTotal(splitItems, index, splitTip, splitTax, splits.length)
        }))
      );
    }
  }, [splitItems, splitTip, splitTax, isSplitting]);

  const handleDeleteReceipt = async () => {
    if (!selectedReceipt) return;

    const confirmed = window.confirm("Are you sure you want to delete this receipt? This action cannot be undone.");
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/delete_receipt?receipt_id=${selectedReceipt.uuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete receipt');
      }

      setSuccessToast({ open: true, message: "Receipt deleted successfully!" });
      await fetchReceiptsList();
      navigate('/receipts');
    } catch (error) {
      console.error('Error deleting receipt:', error);
      setErrorToast({ open: true, message: 'Failed to delete receipt. Please try again.' });
    }
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleOpenImageModal = () => {
    if (isMobile) {
      setShowImageModal(true);
    } else {
      setShowPreview(true);
    }
  };

  const handleDeleteItem = async (itemId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    try {
      await deleteReceiptItem(token, itemId);
      // Remove the item from the local state
      setItems(items.filter(item => item.item_id !== itemId));
      setSuccessToast({ open: true, message: 'Item deleted successfully' });
      
      // Refresh the items to ensure we have the latest data
      await fetchReceiptItemsAndUpdateState(selectedReceipt.uuid);
    } catch (error) {
      console.error('Error deleting item:', error);
      setErrorToast({ open: true, message: 'Failed to delete item. Please try again.' });
    }
  };

  const renderDesktopTable = () => (
    <table className="w-full border-collapse table-fixed">
      <thead>
        <tr className="bg-gray-900/80 border-b border-gray-700/50">
          <th className={`p-3 transition-all duration-300 ease-out ${isSplitting ? 'w-16' : 'w-0'}`}></th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: '40%' }}>Item</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: '12%' }}>Tags</th>
          <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: '28%' }}>Category</th>
          <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: '12%' }}>Price</th>
          <th className="text-center px-4 py-3" style={{ width: '8%' }}></th>
        </tr>
      </thead>
      <tbody>
        {items && items.length > 0 ? (
          items.map((item) => (
            <ReceiptItemRow
              key={item.item_id}
              item={item}
              isMobile={false}
              onEdit={handleItemEdit}
              onDelete={(itemId) => setDeleteConfirmation({ open: true, itemId })}
              isSplitting={isSplitting}
              splitItems={splitItems}
              splitColors={splitColors}
              splits={splits}
              handleItemSelect={handleItemSelect}
              expandedItems={expandedItems}
              toggleItemExpand={toggleItemExpand}
              getEffectiveValue={getEffectiveValue}
              isFieldEdited={isFieldEdited}
              parseCurrencyString={parseCurrencyString}
            />
          ))
        ) : (
          <tr>
            <td colSpan={6} className="text-center p-4 text-gray-400">
              No items found for this receipt
            </td>
          </tr>
        )}
        {selectedReceipt?.maybe_receipt_tip && (
          <tr className="border-t border-gray-700/50 bg-blue-900/20">
            <td className={`p-3 transition-all duration-500 ease-out ${isSplitting ? 'w-16' : 'w-0'}`}></td>
            <td className="px-4 py-3 text-left text-sm text-gray-400">Tip</td>
            <td className="px-4 py-3 text-left"></td>
            <td className="px-4 py-3 text-left"></td>
            <td className="p-3 whitespace-nowrap"><div className="flex justify-end items-center gap-1"><span>${parseCurrencyString(getEffectiveValue(selectedReceipt, 'maybe_receipt_tip')).toFixed(2)}</span><span className="w-7 inline-block"></span></div></td>
            <td className="px-4 py-3 text-center"></td>
          </tr>
        )}
        {selectedReceipt?.maybe_receipt_tax && (
          <tr className="border-t border-gray-700/50 bg-purple-900/20">
            <td className={`p-3 transition-all duration-500 ease-out ${isSplitting ? 'w-16' : 'w-0'}`}></td>
            <td className="px-4 py-3 text-left text-sm text-gray-400">Tax</td>
            <td className="px-4 py-3 text-left"></td>
            <td className="px-4 py-3 text-left"></td>
            <td className="p-3 whitespace-nowrap"><div className="flex justify-end items-center gap-1"><span>${parseCurrencyString(getEffectiveValue(selectedReceipt, 'maybe_receipt_tax')).toFixed(2)}</span><span className="w-7 inline-block"></span></div></td>
            <td className="px-4 py-3 text-center"></td>
          </tr>
        )}
        <tr className="border-t border-gray-600/50 bg-gray-900/50">
          <td className={`p-3 transition-all duration-500 ease-out ${isSplitting ? 'w-16' : 'w-0'}`}></td>
          <td className="px-4 py-4 text-left font-semibold text-white">Total</td>
          <td className="px-4 py-4 text-left"></td>
          <td className="px-4 py-4 text-left"></td>
          <td className="p-3 whitespace-nowrap"><div className="flex justify-end items-center gap-1"><span className="font-semibold text-emerald-400">${parseCurrencyString(getEffectiveValue(selectedReceipt, 'total_cost')).toFixed(2)}</span><span className="w-7 inline-block"></span></div></td>
          <td className="px-4 py-4 text-center"></td>
        </tr>
      </tbody>
    </table>
  );

  const renderMobileTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-900/80 border-b border-gray-700/50">
            {isSplitting && <th className="p-3" style={{ width: '10%' }}></th>}
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: isSplitting ? '50%' : '60%' }}>Item</th>
            <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider" style={{ width: '30%' }}>Price</th>
            <th className="w-[40px]"></th>
          </tr>
        </thead>
        <tbody>
          {items && items.length > 0 ? (
            items.map((item) => (
              <ReceiptItemRow
                key={item.item_id}
                item={item}
                isMobile={true}
                onEdit={handleItemEdit}
                onDelete={(itemId) => setDeleteConfirmation({ open: true, itemId })}
                isSplitting={isSplitting}
                splitItems={splitItems}
                splitColors={splitColors}
                splits={splits}
                handleItemSelect={handleItemSelect}
                expandedItems={expandedItems}
                toggleItemExpand={toggleItemExpand}
                getEffectiveValue={getEffectiveValue}
                isFieldEdited={isFieldEdited}
                parseCurrencyString={parseCurrencyString}
              />
            ))
          ) : (
            <tr>
              <td colSpan={4} className="text-center p-4 text-gray-400">
                No items found for this receipt
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderDeleteConfirmation = () => (
    <Dialog
      open={deleteConfirmation.open}
      onClose={() => setDeleteConfirmation({ open: false, itemId: null })}
    >
      <DialogTitle>Delete Item</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this item? This action cannot be undone.</Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setDeleteConfirmation({ open: false, itemId: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              handleDeleteItem(deleteConfirmation.itemId);
              setDeleteConfirmation({ open: false, itemId: null });
            }}
          >
            Delete
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const handleViewReceipt = (receiptUuid) => {
    navigate(`/receipts/${receiptUuid}`);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Add this function to handle clearing the search input
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleReceiptEditLocal = (field, value) => {
    // This function updates the local state immediately for a smoother UX
    // before the server response comes back
    if (selectedReceipt) {
      setSelectedReceipt(applyLocalReceiptUpdate(selectedReceipt, field, value));
    }
  };

  const handleReceiptEdit = async (field, value) => {
    const token = localStorage.getItem('token');
    if (!token || !selectedReceipt) return;

    try {
      // If this is a date field, ensure we convert to ISO format
      let processedValue = value;
      if (field === 'date') {
        processedValue = formatDateForAPI(value) || value;
      }

      // Update local state immediately for a smoother UX
      handleReceiptEditLocal(field, value);

      await updateReceiptField(token, selectedReceipt.uuid, field, processedValue);
      
      // Refresh both the receipts list and the selected receipt
      await fetchReceiptsList();
      
      // Fetch the updated receipt to refresh the selected receipt data
      try {
        const updatedReceipt = await fetchReceipt(token, selectedReceipt.uuid);
        if (updatedReceipt) {
          setSelectedReceipt(updatedReceipt);
        }
      } catch (error) {
        console.error('Error refreshing receipt:', error);
      }
      
      setSuccessToast({ open: true, message: "Receipt updated successfully!" });
    } catch (error) {
      setErrorToast({ open: true, message: "Failed to update receipt: " + error.message });
      
      // Refresh the receipt to revert any local changes if the server update failed
      try {
        const updatedReceipt = await fetchReceipt(token, selectedReceipt.uuid);
        if (updatedReceipt) {
          setSelectedReceipt(updatedReceipt);
        }
      } catch (secondError) {
        console.error('Error refreshing receipt after failed update:', secondError);
      }
    }
  };

  const handleItemEdit = async (itemId, updates) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // If updates is not an array, convert single update to array format
      const updateArray = Array.isArray(updates) ? updates : [{field: updates.field, value: updates.value}];

      // Apply updates to local state immediately for a smoother UX
      updateArray.forEach(update => {
        handleItemEditLocal(itemId, update.field, update.value);
      });

      // Process updates sequentially
      for (const update of updateArray) {
        await updateReceiptItemField(token, itemId, update.field, update.value);
      }

      // Refresh the items after all updates are complete
      await fetchReceiptItemsAndUpdateState(selectedReceipt.uuid);
      setSuccessToast({ open: true, message: "Item updated successfully!" });
    } catch (error) {
      setErrorToast({ open: true, message: "Failed to update item: " + error.message });
      // Refresh items to revert any local changes if the server update failed
      await fetchReceiptItemsAndUpdateState(selectedReceipt.uuid);
    }
  };

  // Function to fetch receipt items and update state
  const fetchReceiptItemsAndUpdateState = async (uuid) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth?mode=login');
      return;
    }

    try {
      const data = await fetchReceiptItems(token, uuid);
      setItems(data);
    } catch (error) {
      console.error('Error fetching receipt items:', error);
      setErrorToast({ open: true, message: 'Failed to fetch receipt items. Please try again.' });
      setItems([]);
    }
  };

  return (
    <div className={`flex flex-col overflow-hidden ${className}`}>
      <PWAInstallBanner />
      
      
      {/* Loading bar */}
      {isUploading && (
        <LinearProgress 
          color="primary" 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        />
      )}

      <div className="flex flex-grow overflow-hidden">
        {/* Left Pane: Receipt History */}
        <div 
          className={`w-72 flex-shrink-0 bg-gradient-to-b from-gray-850 to-gray-900 overflow-y-auto transition-all duration-300 ease-out receipts-history-pane border-r border-gray-700/30
            ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'sticky top-0 h-[calc(100vh-64px)]'}
            ${(showLeftPane || !isMobile) ? 'translate-x-0' : '-translate-x-full'}
          `}
          style={{ backgroundColor: '#111827' }}
        >
          <div className="flex flex-col bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900/95 sticky top-0 z-10 border-b border-gray-700/50">
            <div className="flex justify-between items-center p-4">
              <div>
                <h2 className="text-lg font-bold text-white">Receipt History</h2>
                <p className="text-xs text-gray-500 mt-0.5">{filteredReceipts.length} receipts</p>
              </div>
              {isMobile && (
                <button
                  onClick={() => setShowLeftPane(false)}
                  className="text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-lg hover:bg-gray-700/50"
                >
                  <FaTimes size={20} />
                </button>
              )}
            </div>
            
            {/* Enhanced Search Input */}
            <div className="px-3 pb-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search receipts..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-8 py-2 text-sm bg-gray-800/60 border border-gray-700/50 rounded-lg
                    text-white placeholder-gray-500
                    focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/50
                    hover:bg-gray-800/80 hover:border-gray-600/60
                    transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300"
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Enhanced Upload Button */}
            {!isMobile && (
              <div className="px-3 pb-3">
                <button
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  className="
                    flex items-center justify-center w-full py-2.5 px-4 
                    bg-gradient-to-r from-blue-600 to-blue-500
                    hover:from-blue-500 hover:to-purple-600
                    text-white font-medium text-sm rounded-lg
                    shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30
                    transition-all duration-300 ease-out
                    disabled:opacity-60 disabled:cursor-not-allowed
                    group
                  "
                >
                  {isUploading ? (
                    <>
                      <CircularProgress size={18} color="inherit" className="mr-2" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 18 }} className="mr-2 group-hover:scale-110 transition-transform" />
                      Upload Receipts
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          {isLoadingReceipts ? (
            <div className="flex justify-center items-center h-48">
              <CircularProgress />
            </div>
          ) : (
            <div className="px-2 py-2 space-y-1.5">
              {filteredReceipts.map((receipt) => {
                const isSelected = selectedReceipt?.uuid === receipt.uuid;
                return (
                  <div
                    key={receipt.uuid}
                    onClick={() => handleReceiptSelect(receipt)}
                    className={`
                      relative group cursor-pointer rounded-lg p-3
                      transition-all duration-200 ease-out
                      ${isSelected 
                        ? 'bg-blue-500/10 border-l-2 border-l-blue-500 border-y border-r border-y-gray-700/30 border-r-gray-700/30' 
                        : 'bg-gray-800/40 border-l-2 border-l-transparent border-y border-r border-transparent hover:bg-gray-700/50 hover:border-l-gray-600'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {/* Receipt Icon */}
                      <div className={`
                        flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center
                        transition-all duration-200
                        ${isSelected 
                          ? 'bg-blue-500/20' 
                          : 'bg-gray-700/60 group-hover:bg-gray-600/60'
                        }
                      `}>
                        <ReceiptIcon sx={{ 
                          fontSize: 18, 
                          color: isSelected ? '#60a5fa' : 'rgba(255,255,255,0.5)'
                        }} />
                      </div>
                      
                      {/* Content - Left aligned */}
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className={`
                          font-medium text-sm truncate leading-tight
                          ${isSelected ? 'text-white' : 'text-gray-300 group-hover:text-white'}
                        `}>
                          {getEffectiveValue(receipt, 'merchant_name')}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {formatDate(getEffectiveValue(receipt, 'date'))}
                          </span>
                          <span className="text-gray-600">·</span>
                          <span className={`
                            text-xs font-medium
                            ${isSelected ? 'text-emerald-400' : 'text-gray-400 group-hover:text-emerald-400'}
                            transition-colors duration-200
                          `}>
                            ${parseCurrencyString(getEffectiveValue(receipt, 'total_cost')).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredReceipts.length === 0 && !isLoadingReceipts && (
                <div className="text-center py-8 text-gray-500">
                  <ReceiptIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                  <p className="mt-2 text-sm">No receipts found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-grow flex flex-col overflow-hidden relative">
          {/* Top bar with menu icon and receipt toggle - hidden on mobile */}
          <div className="hidden sm:block bg-gray-900/95 backdrop-blur-sm border-b border-gray-700/30 w-full z-10 sticky top-0 left-0">
            <div className="flex justify-between items-center px-4 py-3">
              <div className="flex items-center">
                <h2 className="text-lg font-semibold text-white">
                  {selectedReceipt ? (
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400">Items for</span>
                      <span className="text-white">{getEffectiveValue(selectedReceipt, 'merchant_name')}</span>
                    </span>
                  ) : (
                    <span className="text-gray-400">Select a receipt</span>
                  )}
                </h2>
              </div>
            </div>
          </div>

          {/* Items list - Make it scrollable and adjust height dynamically */}
          <div 
            className="flex-grow overflow-y-auto transition-all duration-300 ease-out"
            style={{
              height: isSplitting ? `calc(100% - ${drawerHeight})` : '100%',
              paddingBottom: isSplitting ? drawerHeight : '50px',
            }}
          >
            <div className="p-4">
              <div className="max-w-5xl mx-auto">
                {(isLoadingSelectedReceipt && receiptId) ? (
                  <div className="flex items-center justify-center h-[60vh]">
                    <CircularProgress />
                  </div>
                ) : selectedReceipt ? (
                  <>
                    {/* Back to receipts pill - mobile only */}
                    {isMobile && (
                      <div className="text-left mb-3">
                        <button
                          onClick={() => setShowLeftPane(true)}
                          className="px-3 py-1.5 rounded-full inline-flex items-center gap-1.5
                            bg-gray-800/60 border border-gray-700/50
                            text-gray-300 hover:text-white hover:bg-gray-700/60
                            text-sm font-medium
                            transition-all duration-200"
                        >
                          <FaBars size={14} />
                          Receipts
                        </button>
                      </div>
                    )}
                    
                    {/* Enhanced Metadata Section */}
                    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden shadow-xl mb-4 sm:mb-6">
                      {/* Header with merchant name and actions */}
                      <div className="px-3 py-2 sm:px-5 sm:py-4 border-b border-gray-700/50">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-3">
                          {/* Title row - centered on mobile */}
                          <div className="w-full sm:w-auto">
                            <div className="text-center sm:text-left">
                              <EditableField
                                value={getEffectiveValue(selectedReceipt, 'merchant_name')}
                                onSave={(value) => handleReceiptEdit('merchant_name', value)}
                                label="Merchant Name"
                                isEdited={isFieldEdited(selectedReceipt, 'merchant_name')}
                                className="text-lg sm:text-xl font-bold text-white"
                                floatingEditButton={true}
                              />
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                              {formatDate(getEffectiveValue(selectedReceipt, 'date'))}
                            </p>
                          </div>
                          
                          {/* Action buttons - responsive grid on mobile */}
                          <div className="grid grid-cols-4 sm:flex sm:flex-wrap gap-1.5 sm:gap-2 w-full sm:w-auto">
                            <button
                              onClick={handleExportCSV}
                              className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-lg
                                bg-gray-700/50 text-gray-300 border border-gray-600/50
                                hover:bg-gray-600/50 hover:text-white hover:border-gray-500/50
                                transition-all duration-200"
                            >
                              <FileDownloadIcon sx={{ fontSize: 12 }} className="mr-0.5 sm:mr-1" />
                              Export
                            </button>
                            <button
                              onClick={handleSplitReceipt}
                              className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-lg
                                bg-purple-500/10 text-purple-400 border border-purple-500/30
                                hover:bg-purple-500/20 hover:text-purple-300
                                transition-all duration-200"
                            >
                              <GroupIcon sx={{ fontSize: 12 }} className="mr-0.5 sm:mr-1" />
                              Split
                            </button>
                            <button
                              onClick={handleOpenImageModal}
                              className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-lg
                                bg-blue-500/10 text-blue-400 border border-blue-500/30
                                hover:bg-blue-500/20 hover:text-blue-300
                                transition-all duration-200"
                            >
                              <ReceiptIcon sx={{ fontSize: 12 }} className="mr-0.5 sm:mr-1" />
                              View
                            </button>
                            <button
                              onClick={handleDeleteReceipt}
                              className="inline-flex items-center justify-center px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium rounded-lg
                                bg-red-500/10 text-red-400 border border-red-500/30
                                hover:bg-red-500/20 hover:text-red-300
                                transition-all duration-200"
                            >
                              <DeleteIcon sx={{ fontSize: 12 }} className="mr-0.5 sm:mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats grid - 2x2 on mobile, 4 columns on desktop */}
                      <div className="grid grid-cols-2 sm:grid-cols-4">
                        <div className="px-2 py-1.5 sm:px-5 sm:py-3 border-r border-gray-700/30 border-b sm:border-b-0 border-gray-700/30 text-center sm:text-left">
                          <p className="text-[10px] sm:text-xs text-gray-300 uppercase tracking-wider font-semibold">Date</p>
                          <EditableField
                            value={getEffectiveValue(selectedReceipt, 'date')}
                            displayValue={formatDate(getEffectiveValue(selectedReceipt, 'date'))}
                            onSave={(value) => handleReceiptEdit('date', value)}
                            label="Date"
                            type="date"
                            isEdited={isFieldEdited(selectedReceipt, 'date')}
                            className="text-sm sm:text-base font-medium text-white"
                            floatingEditButton={true}
                          />
                        </div>
                        <div className="px-2 py-1.5 sm:px-5 sm:py-3 sm:border-r border-gray-700/30 border-b sm:border-b-0 border-gray-700/30 text-center sm:text-left">
                          <p className="text-[10px] sm:text-xs text-gray-300 uppercase tracking-wider font-semibold">Total</p>
                          <EditableField
                            value={getEffectiveValue(selectedReceipt, 'total_cost')}
                            onSave={(value) => handleReceiptEdit('total_cost', value)}
                            label="Total"
                            type="number"
                            isEdited={isFieldEdited(selectedReceipt, 'total_cost')}
                            className="text-sm sm:text-base font-bold text-emerald-400"
                            floatingEditButton={true}
                          />
                        </div>
                        <div className="px-2 py-1.5 sm:px-5 sm:py-3 border-r border-gray-700/30 text-center sm:text-left">
                          <p className="text-[10px] sm:text-xs text-gray-300 uppercase tracking-wider font-semibold">Tax</p>
                          <EditableField
                            value={getEffectiveValue(selectedReceipt, 'maybe_receipt_tax')}
                            onSave={(value) => handleReceiptEdit('maybe_receipt_tax', value)}
                            label="Tax"
                            type="number"
                            isEdited={isFieldEdited(selectedReceipt, 'maybe_receipt_tax')}
                            className="text-sm sm:text-base font-medium text-white"
                            floatingEditButton={true}
                          />
                        </div>
                        <div className="px-2 py-1.5 sm:px-5 sm:py-3 text-center sm:text-left">
                          <p className="text-[10px] sm:text-xs text-gray-300 uppercase tracking-wider font-semibold">Tip</p>
                          <EditableField
                            value={getEffectiveValue(selectedReceipt, 'maybe_receipt_tip')}
                            onSave={(value) => handleReceiptEdit('maybe_receipt_tip', value)}
                            label="Tip"
                            type="number"
                            isEdited={isFieldEdited(selectedReceipt, 'maybe_receipt_tip')}
                            className="text-sm sm:text-base font-medium text-white"
                            floatingEditButton={true}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Items Table */}
                    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden shadow-xl">
                      {isMobile ? renderMobileTable() : renderDesktopTable()}
                    </div>
                  </>
                ) : !receiptId ? (
                  <div className="min-h-[60vh] px-4">
                    {/* Back to receipts pill - mobile only */}
                    {isMobile && (
                      <div className="text-left mb-3 pt-3">
                        <button
                          onClick={() => setShowLeftPane(true)}
                          className="px-3 py-1.5 rounded-full inline-flex items-center gap-1.5
                            bg-gray-800/60 border border-gray-700/50
                            text-gray-300 hover:text-white hover:bg-gray-700/60
                            text-sm font-medium
                            transition-all duration-200"
                        >
                          <FaBars size={14} />
                          Receipts
                        </button>
                      </div>
                    )}
                    
                    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                    {/* Decorative background element */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl" />
                    </div>
                    
                    {/* Animated Receipt Icon */}
                    <div className="relative mb-6">
                      <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600/50 flex items-center justify-center shadow-xl">
                        <ReceiptIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)' }} />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <AddIcon sx={{ fontSize: 18, color: '#fff' }} />
                      </div>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {receipts.length === 0 ? 'Welcome to My Receipts AI' : 'Select a Receipt'}
                    </h2>
                    <p className="text-gray-400 mb-8 max-w-md">
                      {receipts.length === 0 
                        ? 'Upload your first receipt to get started with AI-powered expense tracking and insights.'
                        : 'Choose a receipt from the sidebar to view details, or upload a new one.'
                      }
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      {receipts.length > 0 && (
                        <button
                          onClick={() => setShowLeftPane(true)}
                          className="
                            flex items-center justify-center py-3 px-6 min-w-[180px]
                            bg-gray-800/80 border border-gray-600/50 
                            hover:bg-gray-700/80 hover:border-gray-500/50
                            text-white font-medium rounded-xl
                            shadow-lg hover:shadow-xl
                            transition-all duration-200
                            group
                          "
                        >
                          <ReceiptIcon sx={{ fontSize: 20 }} className="mr-2 text-gray-400 group-hover:text-white transition-colors" />
                          Select Receipt
                        </button>
                      )}
                      <button
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        className="
                          flex items-center justify-center py-3 px-6 min-w-[180px]
                          bg-gradient-to-r from-blue-600 to-purple-600 
                          hover:from-blue-500 hover:to-purple-500
                          text-white font-medium rounded-xl
                          shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30
                          transition-all duration-300
                          disabled:opacity-60 disabled:cursor-not-allowed
                          group
                        "
                      >
                        {isUploading ? (
                          <>
                            <CircularProgress size={20} color="inherit" className="mr-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <CloudUploadIcon sx={{ fontSize: 20 }} className="mr-2 group-hover:scale-110 transition-transform" />
                            Upload Receipts
                          </>
                        )}
                      </button>
                    </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Split Receipt Drawer */}
          <SplitReceipts
            isSplitting={isSplitting}
            showSplitModal={showSplitModal}
            splits={splits}
            splitNames={splitNames}
            splitItems={splitItems}
            splitTip={splitTip}
            splitTax={splitTax}
            selectedReceipt={selectedReceipt}
            splitColors={splitColors}
            items={items}
            handleAddSplit={handleAddSplit}
            handleFinishSplit={handleFinishSplit}
            setIsSplitting={setIsSplitting}
            handleSplitNameChange={handleSplitNameChange}
            handleSplitTipChange={handleSplitTipChange}
            handleSplitTaxChange={handleSplitTaxChange}
            handleCloseSplitModal={handleCloseSplitModal}
            handleExportToVenmo={handleExportToVenmo}
          />
        </div>
      </div>

      {/* Floating Action Button for mobile */}
      <UploadFab
        isMobile={isMobile}
        isSplitting={isSplitting}
        handleUpload={handleUploadClick}
        isUploading={isUploading}
      />

      {/* Overlay for mobile when either pane is open */}
      {isMobile && (showLeftPane || showPreview) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => {
            setShowLeftPane(false);
            setShowPreview(false);
          }}
        ></div>
      )}

      {/* Add this hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/heic,image/heif,image/png,application/pdf"
        multiple
        style={{ display: 'none' }}
      />

      {/* Error Toast */}
      <Snackbar 
        open={errorToast.open} 
        autoHideDuration={6000} 
        onClose={() => setErrorToast({ ...errorToast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorToast({ ...errorToast, open: false })} severity="error" sx={{ width: '100%' }}>
          {errorToast.message}
        </Alert>
      </Snackbar>

      {/* Add this at the end, before the closing tag */}
      <ImageModal
        showImageModal={showImageModal}
        setShowImageModal={setShowImageModal}
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        selectedReceipt={selectedReceipt}
        receiptImage={receiptImage}
        isMobile={isMobile}
        rightPaneWidth={rightPaneWidth}
        topOffset={topOffset}
      />

      {/* Update this CSS */}
      <style>
        {`
        table {
          table-layout: fixed;
          width: 100%;
        }
        /* Truncate item name column */
        tbody td:nth-child(2) {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 0;
        }
        /* Don't truncate price column - keep it visible */
        td:nth-last-child(2) {
          overflow: visible;
          text-overflow: clip;
          white-space: nowrap;
        }
        .checkbox-column {
          width: 64px;
          transition: all 0.5s ease-out;
        }
        .checkbox-wrapper {
          transition: all 0.5s ease-out;
        }

        /* Custom scrollbar styles */
        .receipts-history-pane {
          scrollbar-width: thin;
          scrollbar-color: #4B5563 #1F2937;
        }

        .receipts-history-pane::-webkit-scrollbar {
          width: 8px;
        }

        .receipts-history-pane::-webkit-scrollbar-track {
          background: #1F2937;
        }

        .receipts-history-pane::-webkit-scrollbar-thumb {
          background-color: #4B5563;
          border-radius: 4px;
          border: 2px solid #1F2937;
        }

        .receipts-history-pane::-webkit-scrollbar-thumb:hover {
          background-color: #6B7280;
        }

        .ease-custom {
          transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .transition-all {
          transition-property: all;
          transition-duration: 300ms;
        }
        `}
      </style>

      {renderDeleteConfirmation()}
    </div>
  );
}

export default ReceiptsDetail;
